import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { HorizontalTabs } from "../../../../components/HorizontalTabs/HorizontalTabs";
import { Table } from "../../../../components/Table";
import { TableTypes } from "../../../../components/Table/Table.types";
import { OfficeStatus, UserPermissions } from "../../../../enums";
import { Api } from "../../../../services/api";
import { Company } from "../../../../services/api/types/Companies";
import { MakeOffice, OfficeRoom } from "../../../../services/api/types/Offices";
import { getRoomColumns, getMemberColumns } from "../columnData";
import { OfficeForm } from "../OfficeForm";
import {
  OfficeFormHeader,
  OfficeFormType,
} from "../OfficeForm/officeFormSchema";
import { TableContainer } from "./EditOffice.styles";
import { ColumnContainer } from "../../Admin.styles";
import { Member } from "../../../../services/api/types/Roles";
import { useAppSelector } from "../../../../store/hooks";
import { handleAxiosError } from "../../../../services/utils/handleAxiosError";
import { FormikHelpers } from "formik";
import axios from "axios";

export const EditOffice: React.FC<{}> = () => {
  const history = useHistory();
  const userPermissions = useAppSelector(state => state.auth.user.permissions);
  const { officeId } = useParams<{ officeId: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [membersTable, setMembersTable] = useState<Array<Member>>([]);
  const [roomsTable, setRoomsTable] = useState<Array<OfficeRoom>>([]);
  const [office, setOffice] = useState<OfficeFormType>();
  const [companiesMap, setCompaniesMap] = useState<Map<string, Company>>(
    new Map()
  );

  const selectedRooms = useRef<Array<OfficeRoom>>([]);
  const selectedMembers = useRef([]);

  const getCompanies = () => {
    Api.companies
      .get()
      .then(res => {
        const companies = new Map<string, Company>();
        res.data.companies.map(company => {
          companies.set(company.name, company);
        });
        setCompaniesMap(companies);
      })
      .catch(error => {
        console.error(error);
        handleAxiosError(error);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  useEffect(() => {
    fetchOffice();
  }, [officeId]);

  const fetchOffice = async () => {
    try {
      const response = await Api.offices.getOffice(officeId);
      response.data.status = OfficeStatus[response.data.status as number];
      //create array of companies based on the array of response companies
      const companies: Array<string> = [];
      for (const company of response.data.companies) {
        if (company.associated) companies.push(company.name);
      }
      //build office data
      const data: OfficeFormType = {
        status: response.data.status,
        name: response.data.name,
        address: response.data.address,
        companies: companies,
      };
      //set the data
      setOffice(data);
      setMembersTable(response.data.members);
      setRoomsTable(response.data.rooms);
      setIsLoading(false);
    } catch (e) {
      console.error("Fetch Office Error:", e);
    }
  };

  const submitValues = (
    values: OfficeFormType,
    { setErrors }: FormikHelpers<OfficeFormType>
  ) => {
    const data: MakeOffice = {
      status:
        values.status === "Active"
          ? OfficeStatus.Active
          : OfficeStatus.Inactive,
      name: values.name || "",
      address: values.address || "",
      companyIds: (values.companies as Array<string>).map(
        company => (companiesMap.get(company) as Company).id
      ),
    };
    Api.offices
      .updateOffice(officeId, data)
      .then(() => {
        const currentLocation = history.location;
        currentLocation.pathname = "/admin/offices";
        history.push(currentLocation);
      })
      .catch((error: Error) => {
        if (
          axios.isAxiosError(error) &&
          error.response &&
          error.response.data.detail ===
            'duplicate key value violates unique constraint "offices_name_key"'
        ) {
          setErrors({ name: "Name already used" });
        } else {
          console.error("Create Office", error);
        }
      });
  };

  let handleAddRoom;
  if (userPermissions.includes(UserPermissions.OfficeManageRooms)) {
    handleAddRoom = () => {
      const currentLocation = history.location;
      currentLocation.pathname = `/admin/offices/${officeId}/addRoom`;
      history.push(currentLocation);
    };
  }

  let handleAddMember;
  if (userPermissions.includes(UserPermissions.RoleManageMembers)) {
    handleAddMember = () => {
      const currentLocation = history.location;
      currentLocation.pathname = `/admin/offices/${officeId}/addMember`;
      history.push(currentLocation);
    };
  }

  const handleDeleteSelected = async () => {
    //delete room from office
    for (const room of selectedRooms.current) {
      Api.offices.deleteOfficeRoom(room.id);
      roomsTable;
    }
    setRoomsTable(
      roomsTable.filter(
        room =>
          selectedRooms.current.findIndex(curr => curr.id === room.id) === -1
      )
    );
  };

  const hTabs = [
    {
      name: "Members",
      content: (
        <TableContainer>
          <Table
            searchable
            editable
            tableType={TableTypes.Members}
            isLoading={isLoading}
            defaultRows={!membersTable.length ? 0 : 20}
            columns={getMemberColumns()}
            rowsPerPage={[10, 20, 35, 50]}
            data={membersTable}
            selectedRows={selectedMembers}
            //handleDeleteSelected={handleDeleteSelected}
            handleAddTeamMember={
              userPermissions.includes(UserPermissions.OfficeManageMembers)
                ? handleAddMember
                : "disabled"
            }
          />
        </TableContainer>
      ),
    },
    {
      name: "Rooms",
      content: (
        <TableContainer>
          <Table
            searchable
            editable
            tableType={TableTypes.Rooms}
            isLoading={isLoading}
            defaultRows={!roomsTable.length ? 0 : 20}
            columns={getRoomColumns()}
            rowsPerPage={[10, 20, 35, 50]}
            data={roomsTable}
            selectedRows={selectedRooms}
            handleDeleteSelected={handleDeleteSelected}
            handleAddTeamMember={handleAddRoom}
          />
        </TableContainer>
      ),
    },
  ];

  return (
    <ColumnContainer>
      {!isLoading && (
        <>
          <OfficeForm
            header={OfficeFormHeader.edit}
            submitValues={submitValues}
            value={office}
            canSave={userPermissions.includes(
              UserPermissions.OfficeManageDetails
            )}
          />
          <div style={{ width: "100%", paddingTop: "32px" }}>
            <HorizontalTabs tabs={hTabs} />
          </div>
        </>
      )}
    </ColumnContainer>
  );
};
